<template>
    <div class="block-TravelInformation travel-information">
        <component :is="productBlock" v-bind="product" :texts="texts" />

        <template v-if="showTotal && totalPriceOre != null">
            <hr />

            <div v-if="orderLines">
                <named-value v-for="(line, index) in orderLines" :key="index" :name="line.description">
                    {{ line.priceOre | prettyPrice }}
                </named-value>
            </div>

            <named-value class="total-price" :name="texts.total_price">
                {{ totalPriceOre | prettyPrice }}
            </named-value>

            <p v-if="texts.footer" class="footer-text">
                {{ texts.footer }}
            </p>
        </template>
    </div>
</template>

<script>
import NamedValue from "@/components/elements/NamedValue";
import BlockTravelInformationZoneTicket from "./BlockTravelInformationZoneTicket";
import BlockTravelInformationFixedPriceTicket from "./BlockTravelInformationFixedPriceTicket";
import BlockTravelInformationRetireeTicket from "./BlockTravelInformationRetireeTicket";
import BlockTravelInformationControlFee from "./BlockTravelInformationControlFee";
import { mapState, mapGetters } from "vuex";
import {
    LOCATION_FROM,
    LOCATION_VIA,
    LOCATION_TO
} from "@/store/modules/location";
import { product_controlfee } from "@/i18n/translations/midttrafik/da";

export default {
    components: {
        NamedValue,
        BlockTravelInformationZoneTicket,
        BlockTravelInformationFixedPriceTicket,
        BlockTravelInformationRetireeTicket,
        BlockTravelInformationControlFee
    },
    props: {
        showTotal: {
            type: Boolean,
            default: true,
            required: false
        }
    },
    computed: {
        productBlock() {
            if (this.isZoneTicket) return "BlockTravelInformationZoneTicket";

            if (this.isFixedPriceTicket)
                return "BlockTravelInformationFixedPriceTicket";

            if (this.isRetireeTicket)
                return "BlockTravelInformationRetireeTicket";

            if (this.isControlFee)
                return "BlockTravelInformationControlFee";

            return null;
        },
        texts() {
            const txts = {
                ...this.$t("block_travel_information"),
                product_zone_ticket: this.$t("product_zone_ticket"),
                product_retiree_ticket: this.$t("product_retiree_ticket"),
                product_controlfee: this.$t("product_controlfee")
            };

            if (this.isZoneTicket) txts.footer = txts.product_zone_ticket.info;
            return txts;
        },
        product() {
            const startStop = this.getLocationByType(LOCATION_FROM);
            const viaStop = this.getLocationByType(LOCATION_VIA);
            const endStop = this.getLocationByType(LOCATION_TO);

            const startStopName = startStop ? startStop.name : null;
            const startStopZoneNumber = startStop ? startStop.zoneNumber : null;
            const startStopZoneName = startStop ? startStop.zoneName : null;
            const viaStopName = viaStop ? viaStop.name : null;
            const viaStopZoneNumber = viaStop ? viaStop.zoneNumber : null;
            const viaStopZoneName = viaStop ? viaStop.zoneName : null;
            const endStopName = endStop ? endStop.name : null;
            const endStopZoneNumber = endStop ? endStop.zoneNumber : null;
            const endStopZoneName = endStop ? endStop.zoneName : null;

            const passengers = this.passengers.map(passenger => {
                return {
                    id: passenger.passengerType.id,
                    count: passenger.count,
                    name:
                        passenger.passengerType[
                        passenger.count === 1
                            ? "nameSingular"
                            : "namePlural"
                        ][this.$i18n.locale]
                };
            });

            if (this.isZoneTicket) {
                return {
                    title: this.texts.product_zone_ticket.title,
                    passengers,
                    validFrom: this.orderDetails.validFrom,
                    validTo: this.orderDetails.validTo,
                    startStop: startStopName,
                    viaStop: viaStopName,
                    endStop: endStopName,
                    startZone: startStopZoneNumber,
                    startZoneName: startStopZoneName,
                    viaZone: viaStopZoneNumber,
                    viaZoneName: viaStopZoneName,
                    endZone: endStopZoneNumber,
                    endZoneName: endStopZoneName,
                    zoneDistance: this.orderDetails.zoneDistance
                };
            }

            if (this.isFixedPriceTicket) {
                const product = this.orderDetails.product;
                return {
                    title: product.texts[this.$i18n.locale].name,
                    passengers,
                    validFrom: this.orderDetails.validFrom,
                    validTo: this.orderDetails.validTo,
                    description: product.texts[this.$i18n.locale].description,
                    validity: product.texts[this.$i18n.locale].validityText
                };
            }

            if (this.isRetireeTicket) {
                const variant = this.$store.getters[
                    "products/retireeticket/getSelectedProductVariant"
                ];
                if (!variant) return {};

                return {
                    title: variant.texts[this.$i18n.locale].name,
                    description: variant.texts[this.$i18n.locale].validityText,
                    validFrom: this.$store.getters[
                        "products/retireeticket/getValidFrom"
                    ],
                    validTo: this.$store.getters[
                        "products/retireeticket/getValidTo"
                    ],
                    amountOfDays: this.$store.getters[
                        "products/retireeticket/getAmountOfDays"
                    ]
                };
            }
            if (this.isControlFee) {
                const product = this.orderDetails.product;
                return {
                    title: this.texts.product_controlfee.title,
                    date: product.date,
                    description: this.texts.product_controlfee.description,
                    fee: this.$store.getters["products/controlfee/getNumber"],
                    feeDate: this.$store.getters["products/controlfee/getFeeDate"],
                };
            }

            return {};
        },
        orderLines() {
            if (this.isRetireeTicket) {
                const ticketPrice = this.$store.getters[
                    "products/retireeticket/getTotalPriceOre"
                ];
                const shippingPrice = this.$store.getters[
                    "system/snailmailShipmentFeeInOre"
                ];

                if (!ticketPrice) return null;

                return [
                    {
                        description: this.product.title,
                        priceOre: ticketPrice
                    },
                    {
                        description: this.texts.delivery,
                        priceOre: shippingPrice
                    }
                ];
            } else {
                return null;
            }
        },
        totalPriceOre() {
            if (this.isRetireeTicket) {
                return this.$store.getters[
                    "products/retireeticket/getTotalPriceOreWithDelivery"
                ];
            } else {
                if (this.orderPriceOre != null) {
                    return this.orderPriceOre
                } else if (this.orderDetails.calculatedPriceOre != null) {
                    return this.orderDetails.calculatedPriceOre
                }
            }
        },
        ...mapState({
            isZoneTicket: state =>
                state.products.zoneticket.info &&
                state.order.productType ===
                state.products.zoneticket.info.identifier,
            isFixedPriceTicket: state =>
                state.products.fixedpriceticket.info &&
                state.order.productType ===
                state.products.fixedpriceticket.info.identifier,
            isRetireeTicket: state =>
                state.products.retireeticket.info &&
                state.order.productType ===
                state.products.retireeticket.info.identifier,
            isControlFee: state =>
                state.products.controlfee.info &&
                state.order.productType ===
                state.products.controlfee.info.identifier
        }),
        ...mapGetters({
            departureDate: "date/getDepartureDateAsMoment",
            passengers: "passenger/getPassengers",
            passengerCount: "passenger/getPassengerCount",
            orderDetails: "order/getDetails",
            orderPriceOre: "order/getPriceOre",
            getLocationByType: "location/getLocationByType",
            getPassengerCountById: "passenger/getPassengerCountById"
        })
    }
};
</script>

<style scoped lang="scss">
$element-BlockTravelInformation-background: transparent !default;
$element-BlockTravelInformation-color: $body-color !default;
$element-BlockTravelInformation-padding: 0 !default;
$element-BlockTravelInformation-total-price-seperator: 2px dashed rgba(0, 0, 0, 0.1) !default;

.travel-information {
    background: $element-BlockTravelInformation-background;
    padding: $element-BlockTravelInformation-padding;
    color: $element-BlockTravelInformation-color;
}

hr {
    border: none;
    border-top: $element-BlockTravelInformation-total-price-seperator;
    margin-top: 1em;
    margin-bottom: 1em;
}

.total-price {
    font-size: 1.5em;
    font-weight: 600;
}

.footer-text {
    font-size: 0.9rem;
    margin: 1rem 0 0 0;
    font-style: italic;
}
</style>
